/* The editor shell: inspector rail, live preview, and the wiring between them.

   The rail is rebuilt from state on every change rather than surgically
   patched. At this size that is cheaper to reason about than diffing, and it
   guarantees the controls can never drift out of step with the card. */

window.JCARD = window.JCARD || {};

JCARD.editor = (function () {
  const S = () => JCARD.state.get();
  let selected = 'albumTitle';
  let scale = 3.6;           // preview pixels per millimetre

  /* Changes made while a control still has the caret — typing, dragging a
     colour swatch, nudging a number — repaint the card but leave the rail
     standing. Anything that changes which controls exist (font, mode,
     placement) rebuilds it. */
  const QUIET = { quiet: true };

  function h(tag, attrs, kids) {
    const node = document.createElement(tag);
    Object.keys(attrs || {}).forEach((k) => {
      if (k === 'class') node.className = attrs[k];
      else if (k === 'text') node.textContent = attrs[k];
      else if (k.startsWith('on')) node.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] !== null && attrs[k] !== undefined) node.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach((kid) => node.appendChild(kid));
    return node;
  }

  function label(text) { return h('label', { class: 'jc-label', text }); }

  function row(labelText, control) {
    return h('div', { class: 'jc-row' }, [label(labelText), control]);
  }

  function select(value, options, onChange) {
    const node = h('select', { onchange: (e) => onChange(e.target.value) });
    options.forEach(([val, text]) => {
      const opt = h('option', { value: val, text });
      if (String(val) === String(value)) opt.selected = true;
      node.appendChild(opt);
    });
    return node;
  }

  function colorInput(value, onChange) {
    return h('input', {
      type: 'color', value, class: 'jc-color',
      oninput: (e) => onChange(e.target.value, QUIET),
    });
  }

  function checkbox(checked, text, onChange) {
    const box = h('input', { type: 'checkbox', onchange: (e) => onChange(e.target.checked) });
    box.checked = checked;
    return h('label', { class: 'jc-check' }, [box, h('span', { text })]);
  }

  /* ---------- inspector ---------- */

  function typeControls(field, commit) {
    const fontNames = JCARD.fonts.names.map((n) => [n, n]);
    const single = JCARD.fonts.SINGLE_WEIGHT.has(field.font);
    return [
      row('Font', select(field.font, fontNames, (v) => commit((f) => { f.font = v; }))),
      h('div', { class: 'jc-row jc-row-split' }, [
        select(field.weight, single ? [[400, 'Regular']] : [[400, 'Regular'], [700, 'Bold']],
          (v) => commit((f) => { f.weight = Number(v); })),
        h('input', {
          type: 'number', step: '0.5', min: '3', max: '60',
          value: JCARD.state.mmToPt(field.size).toFixed(1),
          oninput: (e) => {
            const pt = parseFloat(e.target.value);
            if (Number.isFinite(pt) && pt > 0) commit((f) => { f.size = JCARD.state.ptToMm(pt); }, QUIET);
          },
        }),
        h('span', { class: 'jc-unit', text: 'pt' }),
      ]),
      h('div', { class: 'jc-row jc-row-split' }, [
        colorInput(field.color, (v, opts) => commit((f) => { f.color = v; }, opts)),
        h('input', {
          type: 'number', step: '0.01', min: '-0.1', max: '0.5', value: field.tracking,
          oninput: (e) => {
            const t = parseFloat(e.target.value);
            if (Number.isFinite(t)) commit((f) => { f.tracking = t; }, QUIET);
          },
        }),
        h('span', { class: 'jc-unit', text: 'em' }),
      ]),
    ];
  }

  function inspector() {
    const state = S();
    const meta = JCARD.state.FIELD_META[selected];
    const field = state.fields[selected];
    const commit = (fn, opts) => JCARD.state.update((s) => fn(s.fields[selected]), opts);

    const box = h('section', { class: 'jc-panel' }, [
      h('div', { class: 'jc-eyebrow', text: 'Selected' }),
      h('div', { class: 'jc-panel-title', text: meta.label }),
    ]);

    if (meta.isTrackList) {
      box.appendChild(row('Mode', select(field.mode, [
        ['text', 'Editable text'], ['blank', 'Blank lines'],
      ], (v) => commit((f) => { f.mode = v; }))));

      if (field.mode === 'text') {
        const count = JCARD.geom.inside.columns.count;
        const counter = h('p', { class: 'jc-hint' });

        const describe = (lines) => {
          const used = lines.filter((l) => l.trim()).length;
          const longest = lines.reduce((n, l) => Math.max(n, l.length), 0);
          counter.textContent =
            `${used}/${count} tracks · longest ${longest}/${meta.maxChars} characters`;
          counter.classList.toggle('is-at-limit', longest >= meta.maxChars);
        };

        const area = h('textarea', {
          rows: 12, spellcheck: 'false',
          oninput: (e) => {
            const lines = JCARD.state.clampTracks(selected, e.target.value.split('\n'));
            // Drop only the padding the model needs, so the box shows what the
            // user typed rather than a dozen trailing blank lines.
            const shown = lines.join('\n').replace(/\n+$/, e.target.value.endsWith('\n') ? '\n' : '');
            if (shown !== e.target.value) {
              const at = Math.min(e.target.selectionStart, shown.length);
              e.target.value = shown;
              e.target.setSelectionRange(at, at);
            }
            describe(lines);
            commit((f) => { f.lines = lines; }, QUIET);
          },
        });
        area.value = field.lines.join('\n').replace(/\n+$/, '');
        describe(field.lines);
        box.appendChild(area);
        box.appendChild(counter);
        box.appendChild(h('p', {
          class: 'jc-hint',
          text: `One track per line, ${count} lines, ${meta.maxChars} characters each. `
            + 'Track 1 sits next to the badge. Blank lines print as empty rules.',
        }));
        box.appendChild(checkbox(field.numbered, 'Number the tracks',
          (v) => commit((f) => { f.numbered = v; }, QUIET)));
      } else {
        box.appendChild(h('p', {
          class: 'jc-hint',
          text: `${JCARD.geom.inside.columns.count} ruled columns, nothing printed.`,
        }));
      }
      typeControls(field, commit).forEach((n) => box.appendChild(n));
      return box;
    }

    const maxLines = meta.maxLines || 1;
    const counter = h('p', { class: 'jc-hint' });

    function describe(value) {
      const lines = value.split('\n');
      const longest = lines.reduce((n, l) => Math.max(n, l.length), 0);
      counter.textContent = maxLines > 1
        ? `${lines.length}/${maxLines} lines · longest ${longest}/${meta.maxChars} characters`
        : `${longest}/${meta.maxChars} characters`;
      counter.classList.toggle('is-at-limit', longest >= meta.maxChars || lines.length >= maxLines);
    }

    const onInput = (e) => {
      const clamped = JCARD.state.clamp(selected, e.target.value);
      if (clamped !== e.target.value) {
        const at = Math.min(e.target.selectionStart, clamped.length);
        e.target.value = clamped;
        e.target.setSelectionRange(at, at);
      }
      describe(clamped);
      commit((f) => { f.text = clamped; }, QUIET);
    };

    const input = maxLines > 1
      ? h('textarea', { rows: maxLines, spellcheck: 'false', oninput: onInput })
      : h('input', { type: 'text', maxlength: meta.maxChars, oninput: onInput });

    input.value = field.text;
    describe(field.text);
    box.appendChild(input);
    box.appendChild(counter);

    if (maxLines > 1) {
      box.appendChild(h('p', {
        class: 'jc-hint', text: `Up to ${maxLines} lines. Lines never wrap.`,
      }));
    }
    if (selected.startsWith('attribution')) {
      box.appendChild(h('p', { class: 'jc-hint', text: 'Clear the field to remove it from the card.' }));
    }
    typeControls(field, commit).forEach((n) => box.appendChild(n));
    return box;
  }

  function fieldList() {
    const state = S();
    const box = h('section', { class: 'jc-panel' }, [
      h('div', { class: 'jc-eyebrow', text: 'Fields' }),
    ]);
    let page = null;
    JCARD.state.FIELD_ORDER.forEach((id) => {
      const meta = JCARD.state.FIELD_META[id];
      if (meta.page !== page) {
        page = meta.page;
        box.appendChild(h('div', { class: 'jc-subhead', text: page === 'outside' ? 'Outside' : 'Inside' }));
      }
      const field = state.fields[id];
      const swatch = h('span', { class: 'jc-swatch' });
      swatch.style.background = field.color;
      box.appendChild(h('button', {
        class: `jc-field${id === selected ? ' is-selected' : ''}`,
        onclick: () => { selected = id; paint(); },
      }, [swatch, h('span', { class: 'jc-field-name', text: meta.label }),
        h('span', { class: 'jc-field-font', text: field.font })]));
    });
    return box;
  }

  /* ---------- other panels ---------- */

  function coverPanel() {
    const state = S();
    const set = (fn) => JCARD.state.update((s) => fn(s.cover));

    const file = h('input', {
      type: 'file', accept: 'image/*',
      onchange: (e) => {
        const f = e.target.files && e.target.files[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => set((c) => { c.src = reader.result; });
        reader.readAsDataURL(f);
      },
    });

    const box = h('section', { class: 'jc-panel' }, [
      h('div', { class: 'jc-eyebrow', text: 'Cover art' }),
      file,
      row('Placement', select(state.cover.placement, [
        ['inset', 'Inset (template)'], ['bleed', 'Full bleed'], ['full', 'Full panel'],
      ], (v) => set((c) => { c.placement = v; }))),
      row('Scaling', select(state.cover.fit, [
        ['fill', 'Fill (crop)'], ['fit', 'Fit (letterbox)'], ['stretch', 'Stretch'],
      ], (v) => set((c) => { c.fit = v; }))),
    ]);

    if (state.cover.placement === 'full') {
      box.appendChild(checkbox(state.cover.overlayText, 'Show text over image',
        (v) => set((c) => { c.overlayText = v; })));
      box.appendChild(h('p', {
        class: 'jc-hint',
        text: 'Text keeps its per-field colours here — set them in the inspector.',
      }));
    }

    if (state.cover.src) {
      box.appendChild(h('button', {
        class: 'jc-btn jc-btn-quiet', text: 'Remove image',
        onclick: () => set((c) => { c.src = null; }),
      }));
    }
    return box;
  }

  function colorsPanel() {
    const state = S();
    const set = (key) => (v, opts) => JCARD.state.update((s) => { s.colors[key] = v; }, opts);
    return h('section', { class: 'jc-panel' }, [
      h('div', { class: 'jc-eyebrow', text: 'Card colours' }),
      row('Background', colorInput(state.colors.background, set('background'))),
      row('Rules', colorInput(state.colors.rules, set('rules'))),
      row('Bars & badges', colorInput(state.colors.bars, set('bars'))),
      row('Badge letter', colorInput(state.colors.badgeText, set('badgeText'))),
      h('p', { class: 'jc-hint', text: 'Text colours are set per field in the inspector.' }),
    ]);
  }

  function togglesPanel() {
    const state = S();
    const set = (key) => (v) => JCARD.state.update((s) => { s.toggles[key] = v; });
    return h('section', { class: 'jc-panel' }, [
      h('div', { class: 'jc-eyebrow', text: 'Elements' }),
      h('div', { class: 'jc-subhead', text: 'Outside' }),
      checkbox(state.toggles.dividerRule, 'Divider rule above title', set('dividerRule')),
      checkbox(state.toggles.spineBars, 'Spine end bars', set('spineBars')),
      h('div', { class: 'jc-subhead', text: 'Inside' }),
      checkbox(state.toggles.blockDivider, 'Rule between A and B', set('blockDivider')),
      checkbox(state.toggles.sideBars, 'Side bars beside A and B', set('sideBars')),
      checkbox(state.toggles.sideBadges, 'A and B badges', set('sideBadges')),
    ]);
  }

  function exportPanel() {
    const state = S();
    const status = h('p', { class: 'jc-hint', id: 'jc-status', text: '' });

    async function guard(fn, working) {
      status.textContent = working;
      try {
        await fn();
        status.textContent = 'Done.';
      } catch (err) {
        console.error(err);
        status.textContent = `Failed: ${err.message}`;
      }
    }

    return h('section', { class: 'jc-panel' }, [
      h('div', { class: 'jc-eyebrow', text: 'Export' }),
      row('Duplex flip', select(state.duplex, [
        ['long', 'Flip on long edge'], ['short', 'Flip on short edge'],
      ], (v) => JCARD.state.update((s) => { s.duplex = v; }))),
      checkbox(state.toggles.cropMarks, 'Crop and fold marks', (v) => JCARD.state.update((s) => { s.toggles.cropMarks = v; })),
      checkbox(state.toggles.foldGuides, 'Print fold guide lines', (v) => JCARD.state.update((s) => { s.toggles.foldGuides = v; })),
      h('button', {
        class: 'jc-btn jc-btn-primary', text: 'Download PDF',
        onclick: () => guard(() => JCARD.exporter.download(S()), 'Building PDF…'),
      }),
      h('button', {
        class: 'jc-btn', text: 'Alignment test page',
        onclick: () => guard(() => JCARD.exporter.alignmentTest(S()), 'Building test page…'),
      }),
      h('button', {
        class: 'jc-btn', text: 'Copy shareable link',
        onclick: () => {
          const url = `${location.origin}${location.pathname}#${JCARD.state.toHash()}`;
          navigator.clipboard.writeText(url).then(
            () => { status.textContent = 'Link copied. Cover art is not included.'; },
            () => { status.textContent = url; }
          );
        },
      }),
      h('button', {
        class: 'jc-btn jc-btn-quiet', text: 'Reset to template',
        onclick: () => { JCARD.state.reset(); },
      }),
      status,
    ]);
  }

  /* ---------- preview ---------- */

  function mountPage(hostId, svg, caption) {
    const host = document.getElementById(hostId);
    host.innerHTML = '';
    svg.setAttribute('width', JCARD.geom.CARD_W * scale);
    svg.setAttribute('height', JCARD.geom.CARD_H * scale);
    svg.addEventListener('click', (e) => {
      const hit = e.target.closest('[data-field]');
      if (!hit) return;
      selected = hit.getAttribute('data-field');
      paint();
    });
    host.appendChild(h('div', { class: 'jc-caption', text: caption }));
    host.appendChild(svg);
  }

  function paint(_state, opts) {
    const state = S();

    if (!opts || !opts.quiet) {
      const rail = document.getElementById('jc-rail');
      rail.innerHTML = '';
      [inspector(), fieldList(), coverPanel(), colorsPanel(), togglesPanel(), exportPanel()]
        .forEach((n) => rail.appendChild(n));
    }

    mountPage('jc-page1', JCARD.render.buildOutside(state), 'Page 1 — front / outside');
    mountPage('jc-page2', JCARD.render.buildInside(state), 'Page 2 — back / inside');

    document.querySelectorAll(`#jc-page1 [data-field="${selected}"], #jc-page2 [data-field="${selected}"]`)
      .forEach((n) => n.classList.add('is-selected'));

    /* A newly chosen family paints once with fallback metrics, then again for
       real once its bytes arrive. Only a load that brought something new
       triggers the second pass, so this cannot cycle. */
    JCARD.fonts.ensureFor(state).then((arrived) => {
      if (arrived) paint(null, { quiet: true });
    });
  }

  function start() {
    const zoom = document.getElementById('jc-zoom');
    if (zoom) {
      zoom.addEventListener('input', (e) => {
        scale = Number(e.target.value);
        paint();
      });
    }

    JCARD.state.subscribe(paint);

    if (location.hash.length > 1) JCARD.state.fromHash(location.hash.slice(1));

    JCARD.fonts.ensureFor(JCARD.state.get())
      .then(() => {
        const loading = document.getElementById('jc-loading');
        if (loading) loading.remove();
        paint();
      })
      .catch((err) => {
        document.getElementById('jc-loading').textContent = `Could not load fonts: ${err.message}`;
      });
  }

  return { start, paint };
})();
